import React, { useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { LinkContext } from "../../LinkProvider";
import AnimatedCTA from "../AnimatedCTA";
import tradingPhoto from "../../img/home/trading-photo.webp";

const ChevronDown = ({ size = 24, color = "#020202", className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const SPREADS = [
  { instrument: "XAUUSD", spread: "1.1" },
  { instrument: "EURUSD", spread: "0.4" },
  { instrument: "BRENT",  spread: "0.3" },
  { instrument: "US30",   spread: "3.4" },
  { instrument: "AAPL",   spread: "2.5" },
];

const ITEMS = [
  { tk: "home.trading.item1.title", dk: "home.trading.item1.desc" },
  { tk: "home.trading.item2.title", dk: "home.trading.item2.desc" },
  { tk: "home.trading.item3.title", dk: "home.trading.item3.desc" },
  { tk: "home.trading.item4.title", dk: "home.trading.item4.desc" },
];

function TradingDesk() {
  const { t } = useTranslation();
  const { link } = useContext(LinkContext);
  const [open, setOpen] = useState(0);

  return (
    <section className="mt">
      <div className="glcontainer">

        {/* Row 1: cost snapshot + photo */}
        <div className="flex flex-col xl:flex-row gap-[29px] items-stretch mb-[80px] xl:mb-[120px]">

          {/* Table card */}
          <div className="backdrop-blur-[10px] bg-[rgba(0,0,0,0.03)] border border-[rgba(0,0,0,0.1)] rounded-[20px] p-[25px] w-full xl:w-[391px] shrink-0 flex flex-col">
            <h3 className="font-['Montserrat'] font-semibold text-[24px] text-[#020202] leading-[32px] mb-[24px]">
              {t("home.costs.title")}
            </h3>

            <table className="w-full">
              <thead>
                <tr className="border-b border-[rgba(0,0,0,0.1)]">
                  <th className="text-left pb-[4px] font-['Inter'] font-semibold text-[12px] text-[#c1c1c1] leading-[16px]">
                    {t("home.costs.col.instrument")}
                  </th>
                  <th className="text-right pb-[4px] font-['Inter'] font-semibold text-[12px] text-[#c1c1c1] leading-[16px]">
                    {t("home.costs.col.spread")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {SPREADS.map((row, i) => (
                  <tr key={i} className={i > 0 ? "border-t border-[rgba(0,0,0,0.05)]" : ""}>
                    <td className="py-[12px] font-['Inter'] font-medium text-[14px] text-[#020202] tracking-[0.28px] leading-[20px]">
                      {row.instrument}
                    </td>
                    <td className="py-[12px] text-right font-['Inter'] font-medium text-[14px] text-[#ee5414] tracking-[0.28px] leading-[20px]">
                      {row.spread}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="border-t border-[rgba(0,0,0,0.1)] pt-[24px] mt-[24px] text-center">
              <span className="font-['Inter'] font-semibold text-[12px] text-[#c1c1c1] leading-[16px]">
                {t("home.costs.updated")}
              </span>
            </div>
          </div>

          {/* Photo */}
          <div className="rounded-[20px] overflow-hidden flex-1 min-h-[260px] xl:h-[443px]">
            <img
              src={tradingPhoto}
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Row 2: left text + right accordion */}
        <div className="flex flex-col xl:flex-row gap-[60px] xl:gap-[116px] items-start">

          {/* Left */}
          <div className="flex flex-col gap-[40px] xl:w-[504px] shrink-0">
            <div className="flex flex-col gap-[20px] xl:gap-[40px]">
              <h2 className="font-46-28 break-words hyphens-auto">{t("home.trading.title")}</h2>
              <p className="font-18-14">{t("home.trading.desc")}</p>
            </div>
            <AnimatedCTA href={link} className="self-start">{t("btn.start-trading")}</AnimatedCTA>
          </div>

          {/* Accordion */}
          <div className="flex flex-col gap-[22px] xl:gap-[28px] w-full xl:flex-1">
            {ITEMS.map((item, i) => {
              const isOpen = open === i;
              return (
                <div key={i}>
                  <button
                    onClick={() => setOpen(isOpen ? null : i)}
                    className="w-full flex items-center justify-between text-left gap-[16px] cursor-my"
                  >
                    <span className="font-20-18">{t(item.tk)}</span>
                    <ChevronDown
                      size={24}
                      color="#020202"
                      className={`shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  {isOpen && (
                    <p className="font-18-14 mt-[8px] xl:max-w-[497px]">{t(item.dk)}</p>
                  )}
                  <div className="border-t border-[rgba(0,0,0,0.1)] mt-[16px] xl:mt-[22px]" />
                </div>
              );
            })}
          </div>

        </div>
      </div>
    </section>
  );
}

export default TradingDesk;
